import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { ComponentStore, tapResponse } from "@ngrx/component-store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { exhaustMap, switchMap, tap } from "rxjs/operators";
import { AgsLookupResponseDto } from "src/app/common/dto/region/ags-lookup-response";
import { CreateRegionRequestDto } from "src/app/common/dto/region/create-region-request";
import { DynamicSnackBar } from "src/app/core/material/snack-bar/snack-bar";
import { ProjectConfigurationService } from "src/app/core/services/project-configuration.service";
import { CREATE_MUNICIPALITY_I18N } from "./create-municipality-dialog.constants";

export type CreateMunicipalityStep = "ags" | "details";
export type AgsLookupError = "notFound" | "alreadyExists" | "unknown";

export interface CreateMunicipalityDialogState {
  step: CreateMunicipalityStep;
  agsLookup: AgsLookupResponseDto | null;
  agsLookupLoading: boolean;
  agsLookupError: AgsLookupError | null;
  creating: boolean;
}

const toLookupError = (error: HttpErrorResponse): AgsLookupError => {
  if (error?.status === 404) {
    return "notFound";
  }

  return error?.status === 409 ? "alreadyExists" : "unknown";
};

@Injectable()
export class CreateMunicipalityDialogComponentStore extends ComponentStore<CreateMunicipalityDialogState> {
  private readonly projectConfigurationService = inject(ProjectConfigurationService);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly snackbar = inject(DynamicSnackBar);
  private readonly translate = inject(TranslateService);

  readonly vm$ = this.select((state) => state);
  readonly agsLookup$ = this.select((state) => state.agsLookup);

  readonly clearAgsLookupError = this.updater((state) => ({ ...state, agsLookupError: null }));

  readonly lookupAgs = this.effect((ags$: Observable<string>) =>
    ags$.pipe(
      tap(() => this.patchState({ agsLookupLoading: true, agsLookupError: null })),
      switchMap((ags) =>
        this.projectConfigurationService.getRegionByAgs(ags).pipe(
          tapResponse(
            (agsLookup) => this.patchState({ step: "details", agsLookup, agsLookupLoading: false }),
            (error: HttpErrorResponse) =>
              this.patchState({ agsLookupLoading: false, agsLookupError: toLookupError(error) })
          )
        )
      )
    )
  );

  readonly createMunicipality = this.effect((request$: Observable<CreateRegionRequestDto>) =>
    request$.pipe(
      tap(() => this.patchState({ creating: true })),
      exhaustMap((request) =>
        this.projectConfigurationService.postRegion(request).pipe(
          tapResponse(
            () => {
              this.patchState({ creating: false });
              this.notify("success", "CREATE_SUCCESS");
              this.dialogRef.close(true);
            },
            () => {
              this.patchState({ creating: false });
              this.notify("error", "CREATE_ERROR");
            }
          )
        )
      )
    )
  );

  constructor() {
    super({ step: "ags", agsLookup: null, agsLookupLoading: false, agsLookupError: null, creating: false });
  }

  /**
   * `isMain: false` — the main overlay container only exists inside the regions wrapper, not in the
   * configuration area, so the snack bar has to go to the global CDK overlay (as in `CAModulesTabComponent`).
   */
  private notify(type: "success" | "error", key: string): void {
    this.snackbar[type](this.translate.instant(`${CREATE_MUNICIPALITY_I18N}.${key}`), undefined, undefined, false);
  }
}
